import { CAR_CONFIGS } from './data/cars';
import { TRACK_CONFIGS } from './data/tracks';

export interface GameSelection {
  carId: number;
  trackId: number;
}

export interface SelectionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SelectionState {
  get(): GameSelection;
  set(next: GameSelection): void;
}

const STORAGE_KEY = 'cars-game-selection-v1';
const FALLBACK_ID = 1;

export function getSortedRegistryIds(registry: Record<number, unknown>): number[] {
  return Object.keys(registry)
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0)
    .sort((a, b) => a - b);
}

export function pickExistingId(id: number, availableIds: readonly number[]): number {
  return availableIds.includes(id)
    ? id
    : availableIds.length > 0
      ? Math.min(...availableIds)
      : FALLBACK_ID;
}

function readPositiveInteger(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value.trim());
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function normalizeSelection(
  value: Partial<GameSelection> | null | undefined,
  trackIds: readonly number[],
  carIds: readonly number[],
): GameSelection {
  return {
    carId: pickExistingId(readPositiveInteger(value?.carId) ?? FALLBACK_ID, carIds),
    trackId: pickExistingId(readPositiveInteger(value?.trackId) ?? FALLBACK_ID, trackIds),
  };
}

function getBrowserStorage(): SelectionStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadSelection(
  storage: SelectionStorage | null = getBrowserStorage(),
  trackIds: readonly number[] = getSortedRegistryIds(TRACK_CONFIGS),
  carIds: readonly number[] = getSortedRegistryIds(CAR_CONFIGS),
): GameSelection {
  const fallback = normalizeSelection(undefined, trackIds, carIds);
  if (!storage) {
    return fallback;
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return fallback;
    }

    const parsed: unknown = JSON.parse(raw);
    return normalizeSelection(isRecord(parsed) ? parsed : undefined, trackIds, carIds);
  } catch {
    return fallback;
  }
}

const trackIds = getSortedRegistryIds(TRACK_CONFIGS);
const carIds = getSortedRegistryIds(CAR_CONFIGS);
export function createSelectionState(
  storage: SelectionStorage | null = getBrowserStorage(),
  availableTrackIds: readonly number[] = trackIds,
  availableCarIds: readonly number[] = carIds,
): SelectionState {
  let selection = loadSelection(storage, availableTrackIds, availableCarIds);

  return {
    get(): GameSelection {
      return { ...selection };
    },
    set(next: GameSelection): void {
      selection = normalizeSelection(next, availableTrackIds, availableCarIds);
      if (!storage) {
        return;
      }

      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(selection));
      } catch {
        // Storage can be disabled or full; selection remains usable in memory.
      }
    },
  };
}

export const selectionState: SelectionState = createSelectionState();
